<template>
    <pendo-layout-chooser-v2
        class="layout-chooser"
        :allow-actions="allowActions"
        :default-layouts="defaultLayouts"
        :custom-layouts="customLayouts"
        :ai-layouts="aiLayouts"
        :organization-layouts="organizationLayouts"
        :loading="loading"
        :title="title"
        :apps="appOptions"
        :guide-categories="guideCategoryOptions"
        :layout-category-metagroups="layoutCategoryOptions"
        :selected-app="selectedAppForLayoutChooser"
        :selected-guide-category="selectedGuideCategory"
        :selected-layout-type="selectedLayoutType"
        :empty-option="NO_GUIDE_CATEGORY_OPTION"
        :use-app-display="true"
        :disable-layout-type-chooser="isAddStepToGuide"
        :show-layout-type-chooser="showLayoutTypeChooser"
        :kind="kind"
        :hide-subtext="shouldHideSubtext"
        :app-id="appId"
        :disable-app-selection="disableAppSelection"
        :get-delete-msg-override="getDeleteMsgOverride"
        :visible="visible"
        :should-disable-layout-delete="shouldDisableLayoutDelete"
        @close="onClose"
        @select-app="onSelectApp"
        @select-guide-category="onSelectGuideCategory"
        @select-layout="onSelectLayout"
        @delete-layout="onDeleteLayout"
        @select-layout-type="onSelectLayoutType">
        <template #beforeAiLayouts>
            <div
                v-if="showAILayouts"
                class="not-sure-ai"
                role="region"
                aria-labelledby="ai-prompt-title">
                <div class="not-sure-ai-header">
                    <h4 id="ai-prompt-title">
                        {{ $t('layoutChooser.aiStartPrompt') }}
                    </h4>
                    <p class="helper">
                        {{ $t('layoutChooser.aiHelper') }}
                    </p>
                </div>
                <div class="not-sure-ai-body">
                    <pendo-input
                        v-model="useCase"
                        :placeholder="$t('layoutChooser.aiNotSurePlaceholder')" />
                    <pendo-button
                        data-cy="quick-start-create-guide-button"
                        label="Create guide"
                        type="secondary"
                        :disabled="!useCase"
                        icon="sparkles"
                        @click="submitDescribeYourUseCase" />
                </div>

                <div class="ai-layout-section">
                    <h4>{{ $t('layoutChooser.aiLayoutStart') }}</h4>
                    <p>{{ $t('layoutChooser.aiLayoutDescription') }}</p>
                </div>
            </div>
        </template>
        <template #media="{ item, recommendedTagColor }">
            <layout-chooser-preview-card
                :layout="item"
                :recommended-tag-color="recommendedTagColor"
                :is-email-creation="isEmailCreation"
                :is-announcement="isAnnouncement"
                :selected-app-platform="selectedAppPlatform"
                :get-theme-by-id="getThemeById"
                :default-theme-for-selected-app="defaultThemeForSelectedApp" />
        </template>
    </pendo-layout-chooser-v2>
</template>

<script>
import { AppsService } from '@/common/apps/apps.service';
import { getNoGuideCategoryOption } from '@/common/guide-category-multiselect/guide-category-multiselect-cell.vue';
import {
    canUseConversationalAI,
    createGuideFromLayout,
    createStepsFromLayout,
    isEmbeddedGuide,
    carouselLayouts,
} from '@/guides/helpers/common.util';
import { aiTemplates } from '@/guides/helpers/templates/ai/index';
import { getTemplateParam } from '@/guides/helpers/templates/utils/ai-template-utils';
import { isMobileApp, isWebBasedApp } from '@/utils/apps';
import { cleanupPreviewedGuidesCSS } from '@/utils/guide.utils';
import { canCreateEmail } from '@/vuex/modules/emails/emails-permissions.utils';
import { PendoButton, PendoInput, PendoLayoutChooserV2 } from '@pendo/components';
import { $can } from '@pendo/permissions';
import {
    BuildingBlock,
    BuildingBlockEmails,
    BuildingBlockLayouts,
    MinAgentVersion,
    SdkTools,
} from '@pendo/services/BuildingBlocks';
import { APPS } from '@pendo/services/Constants';
import { isEmailTransactional } from '@pendo/services/EmailUtils';
import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import semver from 'semver';
import { v4 as uuid } from 'uuid';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import LayoutChooserPreviewCard from './layout-chooser-preview-card.vue';

const { getMinSdkVersionForLayout, isFutureSdkLayout, MIN_FLUTTER_CODELESS_SDK_VERSION } = SdkTools;
const { MIN_EMBEDDED_GUIDES_AGENT_VERSION } = MinAgentVersion;
const { EMAIL_PLATFORM } = APPS;
import { ADOPT_PRODUCT_LINES } from '@/vuex/modules/product-lines/product-lines.utils';

export default {
    name: 'LayoutChooserV2',
    components: {
        PendoLayoutChooserV2,
        PendoInput,
        PendoButton,
        LayoutChooserPreviewCard,
    },
    props: {
        newGuideName: {
            type: String,
            default: 'Unnamed',
        },
        isAnnouncement: {
            type: Boolean,
            default: false,
        },
        appId: {
            type: Number,
            default: null,
        },
        title: {
            type: String,
            default: 'Create Guide',
        },
        isEmailCreation: {
            type: Boolean,
            default: false,
        },
        isOrchestrate: {
            type: Boolean,
            default: false,
        },
        experimentId: {
            type: String,
            default: null,
        },
        experimentVariant: {
            type: String,
            default: '',
        },
        location: {
            type: String,
            default: '',
        },
        disableAppSelection: {
            type: Boolean,
            default: false,
        },
        guide: {
            type: Object,
            default: null,
        },
        visible: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['on-guide-created', 'close', 'select-layout'],
    data () {
        return {
            selectedApp: null,
            selectedGuideCategory: null,
            NO_GUIDE_CATEGORY_OPTION: null,
            useStepIdForNewGuideCreation: false,
            defaultLayouts: [],
            customLayouts: [],
            organizationLayouts: [],
            aiLayouts: [],
            loading: false,
            allowActions: {
                delete: false,
            },
            useCase: '',
            selectedLayoutType: 'overlay',
        };
    },
    computed: {
        ...mapGetters({
            apps: 'apps/list',
            getFeatureFlag: 'subscription/getFeatureFlag',
            getActiveSub: 'subscription/getActiveSub',
            getAppById: 'apps/getAppById',
            getLayoutsForPlatform: 'layouts/listForPlatform',
            getThemeById: 'themes/getThemeById',
            getThemesForAppId: 'themes/listForAppId',
            isAdminUser: 'auth/isAdmin',
            isRestrictedMobileApp: 'apps/isRestrictedMobileApp',
            isSuperUser: 'auth/isSuper',
            maxPriority: 'guides/maxPriority',
            userHasPermission: 'auth/hasPermission',
            activeOrganization: 'organization/getActive',
            guideCategoriesGroupedByMetaGroup: 'guideCategories/guideCategoriesGroupedByMetaGroup',
            layoutCategoriesGroupedByMetaGroup: 'layouts/layoutCategoriesGroupedByMetaGroup',
            getLayoutCategories: 'layouts/layoutCategories',
            hasSegmentFlag: 'auth/hasSegmentFlag',
            getActiveEmail: 'emails/getActiveEmail',
            hasProductLine: 'productLines/hasProductLinePurchased',
            guideCategoryByGuideId: 'guideCategories/guideCategoryByGuideId',
            getActiveJourney: 'journeys/getActiveJourney',
            isAdoptEndUser: 'subscription/isAdoptEndUser',
            trainingAttributes: 'subscription/trainingAttributes',
        }),
        productName () {
            return this.isAdoptEndUser ? this.trainingAttributes?.productName || 'OEM' : 'Pendo';
        },
        isAddStepToGuide () {
            return !!this.guide;
        },
        isEmbeddedGuide () {
            return isEmbeddedGuide(this.guide);
        },
        appOptions () {
            let { apps, isAnnouncement, appId, selectedApp, isEmailCreation } = this;

            // announcements are always specific to resource center appId for now
            if (appId && isAnnouncement) {
                return null;
            }

            // filter apps to only what those valid
            // v2 perms and PCC in v1
            apps = apps.filter(({ id }) => {
                if (isEmailCreation) {
                    return canCreateEmail({ appId: id });
                }

                return $can(id)
                    .entity('guide')
                    .action('create')
                    .source('ANY')
                    .target('ANY');
            });

            return apps.reduce((acc, { id, displayName, platform, integrated, applicationFlags, faviconB64, trainingAttributes }) => {
                const isWebBased = isWebBasedApp(platform) && integrated;
                const isLegacyNonIntegrated = platform === 'web' && AppsService.isLegacyApp({ id });
                const isMobile = isMobileApp(platform);
                const isRetroActiveAnalyticsApp = get(applicationFlags, 'mobileRetroactiveAnalyticsApp', false);
                const isMobileIntegrated = isMobile && isRetroActiveAnalyticsApp && integrated;

                if (this.shouldDisableAppSelection) {
                    if (id !== selectedApp.id) {
                        return acc;
                    }
                }

                if (isWebBased || isLegacyNonIntegrated || isMobileIntegrated) {
                    acc.push({
                        id,
                        name: displayName,
                        platform,
                        faviconB64,
                        ...(trainingAttributes && { trainingAttributes }),
                        ...(applicationFlags && { applicationFlags }),
                    });
                }

                return acc;
            }, []);
        },
        canUseGuideCategories () {
            return !this.isAnnouncement && !this.isEmailCreation;
        },
        guideCategoryOptions () {
            if (!this.canUseGuideCategories) return;
            if (this.isAddStepToGuide) return [this.selectedGuideCategory];

            const categoryPlatform = isMobileApp(this.selectedAppPlatform) ? 'mobile' : 'web';

            return this.guideCategoriesGroupedByMetaGroup(categoryPlatform);
        },
        layoutCategoryOptions () {
            if (!this.canUseGuideCategories) return;

            const categoryPlatform = isMobileApp(this.selectedAppPlatform) ? 'mobile' : 'web';

            return this.layoutCategoriesGroupedByMetaGroup(categoryPlatform);
        },
        themesForSelectedApp () {
            // announcements are always specific to resource center appId for now
            if (this.isAnnouncement && this.appId) {
                return this.getThemesForAppId(this.appId);
            }

            const appId = get(this.selectedApp, 'id');

            return this.getThemesForAppId(appId);
        },
        defaultThemeForSelectedApp () {
            return this.themesForSelectedApp.find((theme) => theme.tags.toLowerCase().includes('default'));
        },
        hasV2GuidePermissions () {
            return this.getFeatureFlag('enableGuidePermissionV2');
        },
        canCreateFromLayouts () {
            const appId = get(this.selectedApp, 'id');

            return $can(appId)
                .entity('guideStep')
                .action('create')
                .source('layout')
                .target('ANY');
        },
        canCreateFromScratch () {
            const appId = get(this.selectedApp, 'id');

            return $can(appId)
                .entity('guide')
                .action('create')
                .source('scratch')
                .target('ANY');
        },
        _aiLayouts () {
            if (!this.showAILayouts) return null;

            const platformKey = isMobileApp(this.selectedAppPlatform) ? 'mobile' : 'web';

            return aiTemplates.filter((template) => {
                const platformMatches = template.supportedPlatforms?.[platformKey];
                const deliveryTypeMatches = this.embeddedGuideLayoutsSelected === BuildingBlockLayouts.isEmbeddedLayout(template);

                return platformMatches && deliveryTypeMatches;
            });
        },
        _customLayouts () {
            const {
                isEmailCreation,
                selectedAppPlatform,
                canCreateFromLayouts,
                hasV2GuidePermissions,
                canCreateFromScratch,
            } = this;
            const computedPlatform = isEmailCreation ? EMAIL_PLATFORM : selectedAppPlatform;
            const layouts = this.getLayoutsForPlatform(computedPlatform);

            return layouts.reduce((acc, layout) => {
                const {
                    id,
                    name,
                    supportedPlatforms,
                    tags,
                    steps,
                    orgSharedResourceSettings,
                    trainingSettings,
                } = layout;
                const isPendoDefaultLayout = id.includes('_PENDO_DEFLYT');
                const organizationLayout = !isUndefined(orgSharedResourceSettings);

                if (this.filterByTags(tags)) return acc;

                // logic should only be run if v2 permissions are on
                // once we migrate to v2, we can remove this if wrapper
                if (hasV2GuidePermissions) {
                    // filter out pendo default when you don't have create from scratch
                    if (!canCreateFromScratch && isPendoDefaultLayout) {
                        return acc;
                    }

                    // filter out custom layouts when you don't have create from layouts
                    if (!canCreateFromLayouts && !isPendoDefaultLayout) {
                        return acc;
                    }
                }

                let readOnly = false;
                let isOrgLayout = false;

                let subtext = this.$t('layoutChooser.Custom Layout');
                if (isPendoDefaultLayout) {
                    subtext = this.$t('layoutChooser.default', { productName: this.productName });
                }
                if (organizationLayout) {
                    subtext = this.$t('layoutChooser.Organization Layout');
                    readOnly = true;
                    isOrgLayout = true;
                }
                if (trainingSettings && trainingSettings.enabled) {
                    subtext = this.$t('layoutChooser.Shared Layout');
                }

                acc.push({
                    active: true,
                    supportedPlatforms,
                    id,
                    name,
                    default: isPendoDefaultLayout,
                    subtext,
                    readOnly,
                    isOrgLayout,
                    origin: 'custom',
                    steps,
                    type: tags.split(/[ ,]+/), // accommodate split for commas or spaces
                    ...(trainingSettings && { trainingSettings }),
                });

                return acc;
            }, []);
        },
        _defaultLayouts () {
            const {
                isAnnouncement,
                defaultThemeForSelectedApp,
                selectedAppPlatform,
                isMobileApp,
                hasV2GuidePermissions,
                canCreateFromScratch,
                isEmailCreation,
            } = this;
            const isSuper = this.isSuperUser;
            let theme = isAnnouncement ? null : defaultThemeForSelectedApp;
            let computedPlatform = selectedAppPlatform;
            if (isEmailCreation) {
                theme = BuildingBlockEmails.buildEmailTheme(theme);
                computedPlatform = EMAIL_PLATFORM;
            }
            const layouts = BuildingBlockLayouts.PrebuiltLayouts.DefaultLayouts(theme, {
                platform: computedPlatform,
            });
            const isWebApp = selectedAppPlatform === 'web';

            // logic should only be run if v2 permissions are on
            // once we migrate to v2, we can remove this if wrapper
            if (hasV2GuidePermissions) {
                // remove scratch defaults when you don't have create from scratch
                if (!canCreateFromScratch) {
                    return [];
                }
            }

            return layouts
                .filter(({ name, supportedPlatforms }) => {
                    // filter out super only layouts
                    if (!isSuper && name.includes('SUPER ONLY')) {
                        return false;
                    }

                    if (!this.filterByName(name)) return false;

                    if (!supportedPlatforms) {
                        return isWebApp;
                    }

                    if (isWebApp && !supportedPlatforms.web) {
                        return false;
                    }

                    if (isMobileApp && !supportedPlatforms.mobile && !isEmailCreation) {
                        return false;
                    }

                    return true;
                })
                .map((layout) => {
                    return {
                        ...layout,
                        readOnly: true,
                        default: true,
                        subtext: this.$t('layoutChooser.default', { productName: this.productName }),
                        origin: 'default',
                    };
                });
        },
        disabledPermissions () {
            // we don't disable by v1 role for v2 permissions
            if (!this.hasV2GuidePermissions) {
                return !this.userHasPermission('allowGuideEditViaSource') && !this.isAdminUser && !this.isSuperUser;
            }

            return false;
        },
        preventCodeInjection () {
            return get(this.selectedApp, 'applicationFlags.preventCodeInjection', false);
        },
        selectedAppForLayoutChooser () {
            if (!this.appOptions || !this.selectedApp) return null;

            return this.appOptions.find((app) => app.id === this.selectedApp.id);
        },
        selectedAppPlatform () {
            const platform = get(this.selectedApp, 'platform', 'web');

            return platform === 'extension' ? 'web' : platform;
        },
        isMobileApp () {
            return isMobileApp(this.selectedAppPlatform);
        },
        orgResourcesEnabled () {
            return get(this.activeOrganization, 'organizationFlags.enableResourceSharing', false);
        },
        layoutCategories () {
            return this.getLayoutCategories;
        },
        isAppFlutterCodeless () {
            return this.selectedApp?.framework === 'flutter' && !this.isRestrictedMobileApp(this.selectedApp?.id);
        },
        showLayoutTypeChooser () {
            return (
                this.hasEmbeddedGuidesMinAgentVersion &&
                this.hasProductLine('guides', 'embeddedGuidesEnabled') &&
                !this.isAnnouncement &&
                !this.isEmailCreation &&
                !this.isOrchestrate
            );
        },
        embeddedGuideLayoutsSelected () {
            return this.selectedLayoutType === 'embedded';
        },
        hasEmbeddedGuidesMinAgentVersion () {
            return semver.satisfies(
                semver.coerce(this.selectedApp.agentVersionProd),
                `>=${MIN_EMBEDDED_GUIDES_AGENT_VERSION}`,
            );
        },
        showAILayouts () {
            if (this.isAnnouncement) return false;
            if (this.isOrchestrate) return false;
            if (this.isEmailCreation) return false;
            if (this.isAddStepToGuide) return false;
            if (!this.canCreateFromScratch) return false;

            const segmentFlag = this.hasSegmentFlag('guideConversationalAI');
            const productLineMetadata = this.hasProductLine('guides', 'aIGuideContentEnabled');
            const hasAIGuideConsent = this.getFeatureFlag('enableGenerateAIGuideContent');

            return canUseConversationalAI(segmentFlag, productLineMetadata, hasAIGuideConsent);
        },
        isTransactionalEmailEnabled () {
            return this.hasSegmentFlag('transactionalEmail');
        },
        shouldHideSubtext () {
            return this.isEmailCreation && !this.isTransactionalEmailEnabled;
        },
        isCrossAppJourneysEnabled () {
            return this.hasSegmentFlag('crossAppJourneys');
        },
        shouldDisableAppSelection () {
            return (this.isOrchestrate && !this.isCrossAppJourneysEnabled) || this.isAddStepToGuide;
        },
        kind () {
            return this.isEmailCreation ? this.$t('layoutChooser.kind_template') : this.$t('layoutChooser.kind_layout');
        },
        isResellerSubscription () {
            return this.$productLine(ADOPT_PRODUCT_LINES.adoptReseller);
        },
    },
    async created () {
        this.NO_GUIDE_CATEGORY_OPTION = getNoGuideCategoryOption();
        this.selectedGuideCategory = this.getSelectedGuideCategory();
        this.selectedLayoutType = this.getSelectedLayoutType();

        this.loading = true;
        const promises = [];

        if (this.appId) {
            this.selectedApp = this.getAppById(this.appId);
        } else if (this.appOptions.length) {
            this.selectedApp = this.getAppById(this.appOptions[0].id);
        }

        promises.push(this.getThemeList(), this.getLayoutList());

        if (this.canUseGuideCategories) {
            promises.push(
                this.loadAllMetaGroups(),
                this.loadAllGuideCategories(),
                this.loadLayoutCategories({ items: true }),
            );
        }

        await Promise.all(promises);
        await this.$nextTick();

        if (this.isAdminUser || this.isSuperUser) {
            this.allowActions.delete = true;
        }

        const { defaultLayouts, customLayouts, organizationLayouts, aiLayouts } = await this.buildLayouts();

        this.defaultLayouts = defaultLayouts;
        this.customLayouts = customLayouts;
        this.organizationLayouts = organizationLayouts;
        this.aiLayouts = aiLayouts;

        this.resetLoading();
    },
    methods: {
        ...mapMutations({
            setDescribeYourUseCase: 'guides/setDescribeYourUseCase',
        }),
        ...mapActions({
            getThemeList: 'themes/loadAll',
            getLayoutList: 'layouts/loadAll',
            deleteLayout: 'layouts/delete',
            saveGuide: 'guides/saveGuide',
            loadAllMetaGroups: 'metaGroups/loadAll',
            loadAllGuideCategories: 'guideCategories/loadAll',
            addGuideToCategory: 'guideCategories/addGuideToCategory',
            loadLayoutCategories: 'layouts/loadCategories',
            createOmnichannel: 'emails/createOmnichannel',
        }),
        getDeleteMsgOverride (layout) {
            const isSharedWithAEUs = layout.trainingSettings?.enabled ?? false;

            if (isSharedWithAEUs && this.isResellerSubscription) {

                return this.$t('layoutChooser.deleteMsgOverride.layoutSharedWithAEUs_reseller', { kind: this.kind.toLowerCase() });
            }

            return null;

        },
        getSelectedLayoutType () {
            return this.isEmbeddedGuide ? 'embedded' : 'overlay';
        },
        getSelectedGuideCategory () {
            const guideId = get(this.guide, 'id');

            return this.guideCategoryByGuideId({ guideId }) || this.NO_GUIDE_CATEGORY_OPTION;
        },
        hasPoll (buildingBlocks) {
            return BuildingBlock.findBlocksWithWidgetIds(buildingBlocks, BuildingBlockLayouts.pollWidgetIds).length;
        },
        filterByName (name = '') {
            const { isAnnouncement = false, isEmailCreation = false } = this;

            // filter out non-announcement layouts if annoucement chooser
            if (!!isAnnouncement !== name.includes('Announcement')) {
                return false;
            }

            if (!!isEmailCreation !== name.includes('Email')) {
                return false;
            }

            return true;
        },
        filterByTags (tags = []) {
            const { isAnnouncement = false, isEmailCreation = false } = this;

            // filter out non-announcement layouts if announcement chooser
            if (!!isAnnouncement !== tags.includes('isAnnouncement')) {
                return true;
            }

            if (!!isEmailCreation !== tags.includes('isEmail')) {
                return true;
            }

            return false;
        },
        buildLayouts () {
            return new Promise((resolve) => {
                const {
                    _defaultLayouts,
                    _customLayouts,
                    disabledPermissions,
                    preventCodeInjection,
                    isMobileApp,
                } = this;

                const layouts = _defaultLayouts.concat(_customLayouts).reduce((acc, layout) => {
                    // check for disabled layouts
                    const blankLayout = layout.type[0].includes('Blank') && disabledPermissions;
                    const isEmbeddedGuideLayout = BuildingBlockLayouts.isEmbeddedLayout(layout);
                    const hasCustomCode = BuildingBlockLayouts.layoutHasCustomCode(layout);
                    const codeLayout = hasCustomCode && preventCodeInjection;
                    const layoutOnlyTooltipContent =
                        'Only users who have permission to create layouts and guides from scratch have access to this option.';
                    const preventCodeInjectionTooltipContent =
                        'Layouts containing custom code have been disabled at this time. Please contact your admin for more information.';

                    // save aside mobile carousel 2nd and 3rd steps as we want to use them when choosing
                    // a carousel layout without showing them as separate layouts
                    if (carouselLayouts[layout.id]) {
                        carouselLayouts[layout.id] = layout;

                        return acc;
                    }
                    if (isMobileApp && !this.isEmailCreation) {
                        const minSdkVersion = getMinSdkVersionForLayout(layout);
                        if (semver.satisfies(minSdkVersion, '>2.4.0')) {
                            layout.minSdkVersion = minSdkVersion;
                        }
                    }
                    if (isEmbeddedGuideLayout !== this.embeddedGuideLayoutsSelected) {
                        return acc;
                    }

                    if (isMobileApp && BuildingBlockLayouts.isTooltipLayout(layout)) {
                        if (this.isRestrictedMobileApp(this.selectedApp.id)) {
                            // this filters tooltip layouts out for non codeless apps
                            return acc;
                        }
                        if (
                            this.isAppFlutterCodeless &&
                            (!layout.minSdkVersion || semver.lt(layout.minSdkVersion, MIN_FLUTTER_CODELESS_SDK_VERSION))
                        ) {
                            layout.minSdkVersion = MIN_FLUTTER_CODELESS_SDK_VERSION;
                        }
                    }

                    // filter out default layouts that include not-yet-released features
                    if (isMobileApp && layout.default && isFutureSdkLayout(layout)) {
                        return acc;
                    }

                    // filter out transactional email layouts if user doesn't have transactional email segment flag
                    if (this.isEmailCreation && isEmailTransactional(layout) && !this.isTransactionalEmailEnabled) {
                        return acc;
                    }

                    if (isMobileApp && BuildingBlockLayouts.isCarouselLayout(layout) && layout.default) {
                        layout.readOnly = true;
                    }

                    layout.active = !blankLayout && !codeLayout;

                    if (!layout.active) {
                        layout.tooltipOptions = {
                            content: blankLayout ? layoutOnlyTooltipContent : preventCodeInjectionTooltipContent,
                            multiline: true,
                        };
                    }

                    return acc.concat(layout);
                }, []);

                resolve(this.groupLayouts(layouts));
            });
        },
        groupLayouts (layouts) {
            const defaultLayouts = [];
            const customLayouts = [];
            const organizationLayouts = [];

            layouts.forEach((layout) => {
                // Pendo default layouts
                if (layout.default && !layout.isOrgLayout) {
                    return defaultLayouts.push(layout);
                }

                // Custom layouts
                if (!layout.default && !layout.isOrgLayout) {
                    return customLayouts.push(layout);
                }

                // Organization layouts
                if (layout.isOrgLayout) {
                    return organizationLayouts.push(layout);
                }
            });

            return {
                organizationLayouts,
                customLayouts,
                defaultLayouts,
                aiLayouts: this._aiLayouts,
            };
        },
        async rebuildLayouts () {
            if (this.loading) {
                return;
            }

            this.loading = true;
            const { defaultLayouts, customLayouts, organizationLayouts, aiLayouts } = await this.buildLayouts();
            this.defaultLayouts = defaultLayouts;
            this.customLayouts = customLayouts;
            this.organizationLayouts = organizationLayouts;
            this.aiLayouts = aiLayouts;
            this.resetLoading();
        },
        async resetLoading () {
            await this.$nextTick();
            // why? quickly flashing a loading indicator for anything less than 250ms is extremely jarring to a user.
            setTimeout(() => {
                this.loading = false;
                if (window.pendo) {
                    window.pendo.flexElement(window.document);
                }
            }, 250);
        },
        onSelectApp (app) {
            const previousIsMobileApp = this.isMobileApp;
            this.selectedApp = this.getAppById(app.id);

            if (previousIsMobileApp !== this.isMobileApp) {
                this.selectedGuideCategory = this.NO_GUIDE_CATEGORY_OPTION;
            }

            this.rebuildLayouts();
        },
        onSelectGuideCategory (category) {
            this.selectedGuideCategory = category;
            this.rebuildLayouts();
        },
        submitDescribeYourUseCase () {
            this.setDescribeYourUseCase({ describeYourUseCase: this.useCase });
            const id = uuid();
            const params = {
                appId: this.selectedApp.id,
                conversationId: id,
            };
            const templateType = getTemplateParam({
                isMobile: this.isMobileApp,
                isEmbed: this.embeddedGuideLayoutsSelected,
            });
            if (templateType) {
                params.template = templateType;
            }
            this.cleanupAndRoute({
                name: 'GuidesConversationalAiPage',
                params,
                query: {
                    experimentId: this.experimentId,
                    experimentVariant: this.experimentVariant,
                },
            });
        },
        // Next up, modify layout selection handler to support multiple steps.
        async onSelectLayout (layout) {
            const layoutSteps = BuildingBlockLayouts.getLayoutOrSteps(layout);
            if (layout.isAiTemplate) {
                const id = uuid();
                this.cleanupAndRoute({
                    name: 'GuidesConversationalAiPage',
                    params: {
                        appId: this.selectedApp.id,
                        template: layout.id,
                        conversationId: id,
                    },
                    query: {
                        experimentId: this.experimentId,
                        experimentVariant: this.experimentVariant,
                    },
                });

                return;
            }

            if (this.isAddStepToGuide) {
                const steps = await createStepsFromLayout(
                    this.guide,
                    layout,
                    this.defaultThemeForSelectedApp,
                    undefined,
                    BuildingBlockLayouts.isEmbeddedLayout(layout),
                );

                this.$emit('select-layout', { steps });

                return;
            }

            const customLayoutTheme = layoutSteps.map(({ themeId }) => {
                return this.getThemeById(themeId);
            });
            const { guide: guideFromLayout, isOrgResource, location } = await createGuideFromLayout(layout, {
                app: this.selectedApp,
                isAnnouncement: this.isAnnouncement,
                isEmbeddedLayout: BuildingBlockLayouts.isEmbeddedLayout(layout),
                isEmailTransactional: isEmailTransactional(layout),
                guideName: this.newGuideName,
                theme: this.defaultThemeForSelectedApp,
                isEmail: this.isEmailCreation,
                isAppFlutterCodeless: this.isAppFlutterCodeless,
                isOrchestrate: this.isOrchestrate,
                location: this.location,
                maxPriority: this.maxPriority,
                customLayoutTheme,
                journeyId: this.getActiveJourney?.id,
            });
            const createdGuide = await this.saveGuide({ guide: guideFromLayout, isOrgResource, location });

            if (this.selectedGuideCategory.id) {
                await this.addGuideToCategory({ categoryId: this.selectedGuideCategory.id, guideId: createdGuide.id });
            }

            if (this.isAnnouncement) {
                this.$emit('select-layout', { guide: createdGuide });

                return;
            }

            if (this.experimentId) {
                this.$emit('on-guide-created', createdGuide);

                return;
            }

            this.$emit('on-guide-created', createdGuide);
            this.$emit('close');

            if (this.isEmailCreation || this.isOrchestrate) return;

            await this.cleanupAndRoute({ name: 'GuideDetails', params: { guideId: createdGuide.id } });
        },
        async onDeleteLayout (layout) {
            this.loading = true;
            await this.deleteLayout({ layout });
            ['defaultLayouts', 'customLayouts'].forEach((set) => {
                const foundIndex = this[set].findIndex((l) => l.id === layout.id);
                if (foundIndex !== -1) {
                    this[set].splice(foundIndex, 1);
                }
            });
            this.resetLoading();
        },
        shouldDisableLayoutDelete (layout) {
            return this.isAdoptEndUser && (layout.trainingSettings?.enabled ?? false);
        },
        onClose () {
            const newQuery = { ...this.$route.query };

            if ('openChooser' in newQuery || 'location' in newQuery) {
                delete newQuery.openChooser;
                delete newQuery.location;
                this.$router.replace({ query: newQuery });
            }

            cleanupPreviewedGuidesCSS();
            this.$emit('close');
        },
        createFromSvgOption ({ name }) {
            const layout = this.defaultLayouts.find((layout) => layout.name === name);

            if (layout) {
                this.onSelectLayout(layout);
            } else {
                this.onClose();
            }
        },
        onSelectLayoutType (type) {
            this.selectedLayoutType = type;
            this.rebuildLayouts();
        },
        cleanupAndRoute (route) {
            cleanupPreviewedGuidesCSS();

            return this.$router.push(route);
        },
    },
};
</script>
<style lang="less" scoped>
.layout-chooser {
  &__empty-state {
    .title { font-weight: 600; }
    .description { color: var(--palette-gray-70); margin-top: 8px; }
  }
}

.not-sure-ai {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &-header {
    margin: 0;
    h4 {
      margin: 0 0 4px 0;
      font-family: Inter;
      font-weight: 600;
      font-size: 18px;
      line-height: 24px;
      letter-spacing: 0;
    }
    .helper {
      margin: 0;
      color: var(--palette-gray-70);
      font-size: 14px;
      line-height: 20px;
    }
  }

  &-body {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;

    .pendo-input {
      flex: 1 1 200px;
    }
  }
}

.ai-layout-section {
  margin-top: 24px;

  h4 {
    margin: 0 0 4px 0;
    font-weight: 600;
    font-size: 18px;
    line-height: 24px;
  }

  p {
    margin: 0;
    color: var(--palette-gray-70);
    font-size: 14px;
    line-height: 20px;
  }
}
</style>
